import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Plus } from 'lucide-react';
import useSeoMeta from '../hooks/useSeoMeta';
import seoPaginas from '../content/seoPaginas.json';
import { useAuth } from '../contexts/AuthContext';
import PublicNav from '../components/common/PublicNav';
import PublicFooter from '../components/common/PublicFooter';
import { Eyebrow, Surcos } from '../components/landing/brand';
import { PAGINAS_INFO, type PaginaInfo, type SeccionInfo } from '../content/paginasInfo';
import faqPlanes from '../content/faq/planes.json';
import faqDeExcel from '../content/faq/de-excel-a-app.json';
import { ctaPublico } from '../helpers/ctaPublico';

// Páginas públicas de contenido simple (/planes, /de-excel-a-app). El texto vive
// en src/content/paginasInfo.ts; este componente solo lo dibuja.

const FAQS: Record<PaginaInfo['faq'], { q: string; a: string }[]> = {
  planes: faqPlanes.preguntas,
  'de-excel-a-app': faqDeExcel.preguntas,
};

const Seccion: React.FC<{ s: SeccionInfo }> = ({ s }) => (
  <div style={{ marginTop: 8 }}>
    <h2 className="lp-h3">{s.titulo}</h2>
    {s.parrafos?.map((p) => (
      <p key={p} className="lp-body">
        {p}
      </p>
    ))}
    {s.items && (
      <ul className="lp-body" style={{ listStyle: 'none', padding: 0 }}>
        {s.items.map((t) => (
          <li key={t} style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
            <Check
              aria-hidden="true"
              style={{ flex: 'none', width: 20, height: 20, color: 'var(--lp-pastura)', marginTop: 3 }}
            />
            <span style={{ minWidth: 0 }}>{t}</span>
          </li>
        ))}
      </ul>
    )}
    {s.pasos && (
      <ol className="lp-body" style={{ paddingLeft: 20, listStyle: 'decimal' }}>
        {s.pasos.map((t) => (
          <li key={t} style={{ marginBottom: 8 }}>
            {t}
          </li>
        ))}
      </ol>
    )}
    {s.tabla && (
      <div className="lp-tabla-wrap">
        <table className="lp-tabla">
          <thead>
            <tr>
              {s.tabla.encabezados.map((h, i) => (
                <th key={i} scope="col">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {s.tabla.filas.map((fila) => (
              <tr key={fila[0]}>
                <th scope="row">{fila[0]}</th>
                {fila.slice(1).map((c, i) => (
                  <td key={i}>{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const PaginaInfoPage: React.FC<{ pagina: keyof typeof PAGINAS_INFO }> = ({ pagina }) => {
  const info = PAGINAS_INFO[pagina];
  const { user } = useAuth();
  useSeoMeta(seoPaginas[info.seoKey]);
  const faq = FAQS[info.faq];
  const ctaTo = user
    ? '/dashboard'
    : info.cta.to === '/login'
      ? ctaPublico(user).to
      : info.cta.to;

  return (
    <div className="lp">
      <PublicNav />

      <main>
        <section className="lp-ph">
          <div className="lp-wrap">
            <Eyebrow>{info.eyebrow}</Eyebrow>
            <h1>{info.titulo}</h1>
            <p className="lp-lead" style={{ maxWidth: 760 }}>
              {info.intro}
            </p>
            <div className="lp-ctas">
              <Link className="lp-btn lp-btn--primary" to={ctaTo}>
                {info.cta.to === '/login' ? ctaPublico(user).label : info.cta.label}
              </Link>
              {info.ctaSecundario && (
                <Link className="lp-btn lp-btn--outline" to={info.ctaSecundario.to}>
                  {info.ctaSecundario.label}
                </Link>
              )}
            </div>
          </div>
        </section>

        <section className="lp-sec lp-sec--white">
          <div
            className="lp-wrap"
            style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 36, maxWidth: 900 }}
          >
            {info.secciones.map((s) => (
              <Seccion key={s.titulo} s={s} />
            ))}
          </div>
        </section>

        <section className="lp-sec lp-dark">
          <div className="lp-wrap">
            <h2 className="lp-h2">{info.cierre.titulo}</h2>
            <p className="lp-body" style={{ maxWidth: 760 }}>
              {info.cierre.texto}
            </p>
            <Link className="lp-btn lp-btn--primary" to={ctaTo} style={{ marginTop: 16 }}>
              {ctaPublico(user).label} <ArrowRight aria-hidden="true" />
            </Link>
          </div>
          <Surcos color="blanco" style={{ right: -120, bottom: -260, width: 860 }} />
        </section>

        <section className="lp-sec lp-faq">
          <div className="lp-wrap lp-split">
            <div className="lp-sec-h">
              <Eyebrow>Preguntas</Eyebrow>
              <h2 className="lp-h2">Preguntas frecuentes</h2>
            </div>
            <div>
              {faq.map((item, i) => (
                <details key={item.q} open={i === 0}>
                  <summary>
                    {item.q}
                    <Plus aria-hidden="true" />
                  </summary>
                  <p>{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
};

export default PaginaInfoPage;
